// Block I (UI-16): the task drawer (full screen on a phone: the kit's SidePanel) and the reassign
// picker (a bottom sheet on a phone: the kit's Sheet). Presentational: TeamDashboard.js does the
// requests (the existing PUT and DELETE /api/tasks/:id) through onSave / onToggleDone / onDelete /
// onAssign, each of which throws the server's message, shown here beside the control.
//
// Who may change a task is the server's rule (lib/taskPermissions; Carla's item 1, 2026-10-11): its
// creator, or the business's owner or an admin. canEdit carries the server's answer; a task this
// person may not change opens read only and says why, rather than offering edits that would fail.
import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Field, Input, SegmentedControl, Sheet, SidePanel, StatusPill, Textarea, PRIORITY_OPTIONS } from '../../ui';
import { formatTaskTimeRange, formatTaskDateLine } from '../../utils/taskTime';
import { messageFromError } from '../../utils/apiError';
import { formatDateTime } from '../../utils/formatting';
import { People, STATUS_LABEL, STATUS_TONE, priorityKey } from './TaskViews';
import LinkedNotes from '../Notes/LinkedNotes';   // decision 90 v2: the notes linked to this task
import TaskDueHistory from '../task/TaskDueHistory';

const STORED_PRIORITY = { low: 'Low', medium: 'Medium', high: 'High' };
// lastEditedAt is a real instant, not a task's wall-clock time: the generic timestamp formatter
// (utils/formatting), in the viewer's own zone.
const lastChanged = (iso) => formatDateTime(iso, undefined, { year: undefined }) || 'recently';
const DRAWER_TINT = { todo: 'pastel-yellow', overdue: 'pastel-coral', done: 'pastel-mint' };

// nav (Carla's item 5, 2026-10-12): when the task was opened from a list (Needs attention), Previous and
// Next move through that list without closing: { index, total, label, onPrev, onNext }. Unsaved edits
// are never dropped by a move: the buttons wait until the change is saved or undone.
export function TaskDrawer({ task, status, people, owner, editedBy, canEdit, nav, onClose, onSave, onToggleDone, onDelete, onReassign }) {
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState('');
  const [failure, setFailure] = useState('');

  useEffect(() => {
    if (!task) return;
    setTitle(task.title || '');
    setPriority(priorityKey(task.priority));
    setNotes(task.details || task.description || '');
    setFailure('');
    setBusy('');
  }, [task?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!task) return null;
  const when = [formatTaskDateLine(task), formatTaskTimeRange(task)].filter(Boolean).join(', ');
  const dirty = canEdit && (title.trim() !== (task.title || '') || priority !== priorityKey(task.priority) || notes !== (task.details || task.description || ''));

  const run = async (what, fn) => {
    setBusy(what);
    setFailure('');
    try { await fn(); }
    catch (err) { setFailure(messageFromError(err, 'That change was not saved.')); }
    finally { setBusy(''); }
  };

  const save = () => {
    if (!title.trim()) { setFailure('A task needs a title.'); return; }
    run('save', () => onSave(task, { title: title.trim(), ...(priority ? { priority: STORED_PRIORITY[priority] } : {}), details: notes }));
  };

  const actions = canEdit ? (
    <>
      <Button variant="destructive" onClick={() => onDelete(task)} disabled={!!busy}>Delete</Button>
      <Button variant="secondary" onClick={() => run('done', () => onToggleDone(task))} disabled={!!busy} aria-busy={busy === 'done' || undefined}>
        {task.completed ? 'Reopen task' : 'Mark as done'}
      </Button>
      <Button variant="confirm" onClick={save} disabled={!dirty || !!busy} aria-busy={busy === 'save' || undefined}>{busy === 'save' ? 'Saving…' : 'Save changes'}</Button>
    </>
  ) : <Button variant="tertiary" onClick={onClose}>Close</Button>;

  return (
    <SidePanel open onClose={onClose} title={task.title || 'Task'} color={DRAWER_TINT[status] || 'pastel-yellow'} actions={actions}>
      <div className="td-drawer">
        {nav && (
          <div className="td-drawer__nav" role="group" aria-label={`Move through ${nav.label}`}>
            <Button size="sm" variant="tertiary" onClick={nav.onPrev} disabled={nav.index <= 0 || dirty || !!busy}>← Previous</Button>
            <span className="td-drawer__navat">{nav.index + 1} of {nav.total}</span>
            <Button size="sm" variant="tertiary" onClick={nav.onNext} disabled={nav.index >= nav.total - 1 || dirty || !!busy}>Next →</Button>
          </div>
        )}
        {nav && dirty && <p className="td-tip">Save or undo your changes to move to another task.</p>}
        <div className="td-row-wrap">
          <StatusPill tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</StatusPill>
          {!canEdit && priorityKey(task.priority) && <StatusPill priority={priorityKey(task.priority)} />}
          {task.taskType && <StatusPill tone="neutral">{task.taskType}</StatusPill>}
        </div>

        {!canEdit && (
          <p className="td-tip">
            {owner ? `Only ${owner.name}, the owner or an admin can change this task.` : 'Only the person who created this task, the owner or an admin can change it.'}
          </p>
        )}

        {canEdit ? (
          <Field label="Title">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
        ) : null}

        <div className="td-drawer__row">
          <span className="td-drawer__label">Assigned to</span>
          <div className="td-drawer__people">
            <People task={task} people={people} canEdit={canEdit} onReassign={onReassign} />
            <span className="td-drawer__value">{people.map((u) => u.name).join(', ') || 'No one'}</span>
            {canEdit && <Button size="sm" variant="tertiary" onClick={() => onReassign(task)}>Reassign</Button>}
          </div>
        </div>

        <div className="td-drawer__row">
          <span className="td-drawer__label">Due</span>
          <span className="td-drawer__value">{when || 'No date'}</span>
        </div>

        {/* 2026-10-21: the due date's history (the server says who may read it) */}
        <TaskDueHistory task={task} />

        {editedBy && task.lastEditedAt && (
          <p className="td-tip">Last changed by {editedBy.name}, {lastChanged(task.lastEditedAt)}.</p>
        )}

        {canEdit ? (
          <SegmentedControl label="Priority" options={PRIORITY_OPTIONS} value={priority} onChange={setPriority} />
        ) : null}

        {canEdit ? (
          <Field label="Notes">
            <Textarea rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        ) : (notes && (
          <div className="td-drawer__row">
            <span className="td-drawer__label">Notes</span>
            <span className="td-drawer__value">{notes}</span>
          </div>
        ))}

        {failure && <p className="td-failed" role="alert">{failure}</p>}

        {/* Decision 90 v2: the notes linked to this task, each opening in the Notes editor; "+ New
            note" starts one already linked here. Only a saved task (a numeric id) can have notes. */}
        {/^\d+$/.test(String(task.id ?? '')) && (
          <LinkedNotes kind="task" id={String(task.id)} title="Notes linked to this task"
            emptyText="No notes are linked to this task yet." />
        )}
      </div>
    </SidePanel>
  );
}

// The reassign picker: everyone on the team, ticked if they are on the task.
export function ReassignSheet({ task, users, onClose, onAssign }) {
  const [chosen, setChosen] = useState([]);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState('');

  useEffect(() => {
    if (!task) return;
    setChosen(Array.isArray(task.assignedTeam) ? task.assignedTeam : []);
    setFailure('');
    setBusy(false);
  }, [task?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!task) return null;
  const toggle = (id, on) => setChosen((c) => (on ? [...new Set([...c, id])] : c.filter((x) => x !== id)));
  const save = async () => {
    setBusy(true);
    setFailure('');
    try { await onAssign(task, chosen); onClose(); }
    catch (err) { setFailure(messageFromError(err, 'That change was not saved.')); setBusy(false); }
  };

  return (
    <Sheet open onClose={busy ? undefined : onClose} title={`Assign "${task.title}"`}
      footer={<>
        <Button variant="tertiary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="confirm" onClick={save} disabled={busy} aria-busy={busy || undefined}>{busy ? 'Saving…' : 'Save'}</Button>
      </>}>
      <p className="td-muted">The person who created a task always sees it. Tick everyone else who works on it.</p>
      <ul className="td-pick">
        {users.map((u) => (
          <li key={u.id}>
            <Checkbox label={u.name} checked={chosen.includes(u.id)} onChange={(on) => toggle(u.id, on)} disabled={busy} />
          </li>
        ))}
      </ul>
      {failure && <p className="td-failed" role="alert">{failure}</p>}
    </Sheet>
  );
}
